import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import WorkEmailScreen from '@/karehero-app/access/WorkEmailScreen';
import PartnerCodeScreen from '@/karehero-app/access/PartnerCodeScreen';
import PayrollIdScreen from '@/karehero-app/access/PayrollIdScreen';
import CodeScreen from '@/karehero-app/access/CodeScreen';
import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';
import PlaceholderScreen from '@/karehero-app/tell-us-once/intake/PlaceholderScreen';

// Every route into the app, one flow each, grouped by who takes it.
//
// The sign-up page next door draws one route in full: somebody arriving with
// nothing, answering the funding question and proving they are entitled. That is
// the route we are designing. It is not the only one production runs, and the
// others were only ever described in conversation, so a screen designed for the
// route we drew has been quietly designed for all of them.
//
// Nothing here is new design. The screens are the ones the access flow already
// has, at the step each route actually reaches, and everything a route needs
// that has never been drawn is a placeholder saying what it would have to be.
// Where those cluster is the finding: the invite emails and the moment somebody
// comes back on a link carry most of the routes and have no design at all.

const noop = () => {};

// The bar the steps after registration carry. Same as the sign-up page: no
// initials, because the name is asked for later.
const account = { onSignOut: noop };

const welcome = (
  <WelcomeScreen
    onCreateAccount={noop}
    onSignIn={noop}
    onForgotPassword={noop}
  />
);

// Kinde's hosted registration, over the welcome screen it slides up on. Every
// route passes through it, so it is drawn once and reused.
const kindeRegister = (
  <>
    {welcome}
    <KindeHostedSheet variant='register' onClose={noop} />
  </>
);

/** A step this route has to have and nothing has drawn. */
const missing = (title: string, note: string) => () => (
  <PlaceholderScreen title={title} note={note} />
);

// Three routes end the same way, and the ending is the part that is drawn:
// the link is accepted, and the access question is never asked.
//
// Undrawn and fine. The link carries a token, the token is spent, and there is
// nothing on screen but a loader: this is a placeholder for a step that has no
// design to have.
const arrivingOnALink = missing(
  'Coming back on the link',
  'A loader, and nothing else. The link carries a token, the account is claimed with it, and no access check is asked for: the invite is the entitlement. Undrawn because there is nothing here to draw.',
);

export const onboardingVariantsSpec: HandoverSpec = {
  id: 'onboarding-variants',
  title: 'Onboarding variants',
  description:
    'Six ways into the app, and one of them is designed: the funding question and the three access checks are the route somebody takes when they arrive with nothing. Most people do not. They arrive on a link somebody else sent, which answers the access question before they see a screen. Undrawn is not the same as broken, and most of what is undrawn here is fine — the emails are a link and they work, coming back on one is a loader, and a joining member landing in a care record they did not fill in is an open question rather than a fault. Three things are actually wrong. They are in the first table, and each one is marked again on the flow it happens on.',
  links: [
    { label: 'Getting in: sign up', url: '/onboarding/flows/sign-up' },
    {
      label: 'Onboarding, end to end',
      url: '/master-flows/flows/master-onboarding',
    },
    { label: 'Getting in: prototype', url: '/onboarding/prototype' },
  ],
  tables: [
    {
      title: 'THREE THINGS THAT ARE ACTUALLY WRONG',
      caption:
        'Everything else on this page is a different front door, and the front doors work. These three do not, and two of them are in production rather than only in the prototype.',
      columns: ['What', 'Where it comes from', 'What it does'],
      rows: [
        [
          'The organisation’s own word for the identifier almost never loads',
          'VerifyAccess asks for the verification configuration only when a target organisation id is set, and the one thing that sets one is an organisation-specific sign-in link.',
          'Everybody arriving any other way gets the hardcoded Payroll ID, with helper text about a payslip and an error quoting a work-email subject line. A client who calls it an employee number gets our word for it in three places.',
        ],
        [
          'Nothing reads the verification types',
          'The configuration endpoint returns which methods an organisation actually has, and switching email registration off is supported. VerifyAccessV2 has no prop for it and the view never passes it, so the array is thrown away and only the label is used.',
          'The email field always shows, and our funding question offers all three answers to everybody. At an organisation with email registration off, ‘I’m getting this through work’ is a designed dead end.',
        ],
        [
          'Every access failure ends in try again',
          'Seven error states on the sign-up page, all of them a message under the field.',
          'The only real way out is a KareHero admin creating or verifying the account by hand, and nothing on any screen says that exists.',
        ],
      ],
    },
    {
      title: 'THE INVITE EMAILS',
      caption:
        'Six versions, picked by the type of invite. None of them are drawn, and for four of these routes the email is the first thing somebody sees of KareHero. Not a problem in itself, since a link that works is most of the job. The number that would tell us otherwise is the drop-off between an invite being sent and an account existing, and nobody is measuring it.',
      columns: ['Type', 'Who gets it', 'Subject', 'What it carries'],
      rows: [
        [
          'domain',
          'Anybody whose work email is on an approved domain',
          'Your personal invite to use KareHero',
          'A link, and a QR code as well in the app',
        ],
        [
          'email',
          'Staff their employer listed by name',
          'Your invite from [organisation] to use KareHero',
          'What we help with, then the link',
        ],
        [
          'email-sjp',
          'A client of an SJP adviser',
          '[Client], here’s your invite from [adviser] to use KareHero',
          'The adviser’s own message in quotes, four things we do, then the link',
        ],
        [
          'email-ifa',
          'A client of any other adviser',
          '[Client], here’s your invite from [adviser] to use KareHero',
          'The same, opening on "your financial advisor" instead',
        ],
        [
          'care-circle',
          'Family a member invited',
          'Your personal invite to use KareHero',
          'Who invited them, then the link',
        ],
        [
          'invite-code',
          'Anybody at an organisation set to codes',
          'Your KareHero work email verification code is ●●●●●●',
          'Six characters and no link. Expires in two hours',
        ],
      ],
    },
  ],
  flows: [
    // The route the sign-up page designs, drawn here at the two endings it can
    // have. Which one an organisation gets is a flag on the organisation, and
    // the screens cannot tell you which side of it a client is on.
    {
      journey: 'Employee',
      title: 'Through work',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The entitlement is where they work. The domain is matched against
              the organisations we hold, and everything after that is confirming
              the address is theirs.
            </span>
            <span>
              Two endings, one flag. An organisation on codes gets the screen
              below; an organisation not on codes gets a link, and the same step
              becomes a wait with nothing to type.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'funding-work',
          title: 'Welcome & funding',
          tap: 'The account is made',
          render: () => (
            <FundingScreen
              onContinue={noop}
              onSignOut={noop}
              initialChoice='work'
            />
          ),
          note: (
            <Note title='Problem' colour='cherry' variant='body2'>
              This screen offers all three answers to everybody. Some
              organisations have work email switched off, so for their staff the
              first answer leads nowhere. The configuration that says so is
              already fetched and thrown away, and this screen has never been
              given it.
            </Note>
          ),
        },
        {
          id: 'work-email',
          title: 'Work email',
          tap: '‘I’m getting this through work’',
          render: () => (
            <WorkEmailScreen onSubmit={noop} onBack={noop} account={account} />
          ),
        },
        {
          id: 'code',
          title: 'Check your email',
          tap: 'Continue',
          render: () => (
            <CodeScreen
              email='soph.qualleyy@email.com'
              account={account}
              onVerify={noop}
              onUsePayrollId={noop}
              onSignIn={noop}
              onBack={noop}
            />
          ),
          alt: [
            {
              title: 'A link instead of a code',
              render: missing(
                'Check your inbox',
                'Not drawn. An organisation that is not set to codes gets a link in the email, so there is no field on this step and nothing to submit. They leave the app here and come back verified.',
              ),
            },
          ],
        },
        {
          id: 'verified',
          title: 'You’re in',
          tap: 'The code checks out',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
      ],
    },
    {
      journey: 'Employee',
      title: 'Payroll ID',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              For an organisation that handed staff an identifier rather than
              trusting the domain.
            </span>
            <span>
              Production reads the organisation’s own name for the identifier,
              which is Payroll ID by default and a different word at some
              clients, so the field has to survive being called something else.
            </span>
            <span>
              An organisation can also have this as the only way in, with the
              work email option switched off. There is no design for that: the
              funding question still offers all three.
            </span>
          </Stack>
        </Note>
      ),
      noteAfter: (
        <Note title='Problem' colour='cherry' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The organisation’s own word for this almost never arrives. It is
              only fetched when somebody came in on an organisation-specific
              link, so everybody else gets the default.
            </span>
            <span>
              Which makes three screens wrong at once for a client who calls it
              something else: the field label, the line about it being on a
              payslip, and the error that quotes the subject of the work email.
              Fixing the label alone would leave the other two.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'funding-payroll',
          title: 'Welcome & funding',
          tap: 'The account is made',
          render: () => (
            <FundingScreen
              onContinue={noop}
              onSignOut={noop}
              initialChoice='payroll'
            />
          ),
        },
        {
          id: 'payroll-id',
          title: 'Payroll ID',
          tap: '‘I have a payroll ID’',
          render: () => (
            <PayrollIdScreen onSubmit={noop} onBack={noop} account={account} />
          ),
        },
        {
          id: 'verified-payroll',
          title: 'You’re in',
          tap: 'The ID checks out',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
      ],
    },
    {
      journey: 'Employee',
      title: 'Invited by name',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The link route, and the one the funding question never sees.
              Anybody holding an invite comes through these four steps, whoever
              sent it, so the three routes below are this one with a different
              email at the front.
            </span>
            <span>
              Nothing between the email and You’re in is designed. That is two
              screens carrying most of the people who get into the app.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'employer-email',
          title: 'The invite email',
          tap: 'Their employer adds them',
          render: missing(
            'The employer invite',
            'Not drawn. See the table above. This one goes to the people an HR admin listed, rather than to anybody who turns up with the right domain.',
          ),
        },
        {
          id: 'employer-kinde',
          title: 'Create account (Kinde)',
          tap: 'Tap the link',
          render: () => kindeRegister,
        },
        {
          id: 'employer-link',
          title: 'Coming back on the link',
          tap: 'Set a password',
          render: arrivingOnALink,
        },
        {
          id: 'employer-verified',
          title: 'You’re in',
          tap: 'The token is accepted',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
      ],
    },
    {
      journey: 'Adviser’s client',
      title: 'Referred by their adviser',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Mechanically the route above. What differs is who is in the email
              and who is paying: an adviser refers a named client from the
              portal, and a licence code is spent there if the product needs
              one.
            </span>
            <span>
              The client is never asked how their access is funded, because the
              answer was given by somebody else. Worth holding onto when the
              funding question grows.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'adviser-refers',
          title: 'The adviser refers them',
          tap: 'From the portal',
          render: missing(
            'Refer a client',
            'Not drawn. Three steps in the portal: the client, their details, then a review. The portal has no prototype at all yet, and this is a desktop screen.',
          ),
        },
        {
          id: 'adviser-email',
          title: 'The referral email',
          tap: 'Sent on their behalf',
          render: missing(
            'The referral email',
            'Not drawn. The SJP and adviser versions: their own message to the client in quotes, then the four things we do. The most personal email we send and the least designed.',
          ),
        },
        {
          id: 'adviser-kinde',
          title: 'Create account (Kinde)',
          tap: 'Tap the link',
          render: () => kindeRegister,
        },
        {
          id: 'adviser-link',
          title: 'Coming back on the link',
          tap: 'Set a password',
          render: arrivingOnALink,
        },
        {
          id: 'adviser-verified',
          title: 'You’re in',
          tap: 'The token is accepted',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
      ],
    },
    {
      journey: 'Adviser',
      title: 'Partner code',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The odd one out. Every other route is somebody getting access to
              the app. This is somebody getting the right to hand access out.
            </span>
            <span>
              An adviser types the code their partner gave them, creates an
              organisation, and owns it. They land in the portal, not the app,
              and they only see the member screens if they refer themselves.
            </span>
            <span>
              The funding question puts this beside two answers that lead to a
              field and a home screen. This answer leads somewhere else
              entirely.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'funding-partner',
          title: 'Welcome & funding',
          tap: 'The account is made',
          render: () => (
            <FundingScreen
              onContinue={noop}
              onSignOut={noop}
              initialChoice='partner'
            />
          ),
        },
        {
          id: 'partner-code',
          title: 'Partner code',
          tap: '‘I have a partner code’',
          render: () => (
            <PartnerCodeScreen
              onSubmit={noop}
              onBack={noop}
              account={account}
            />
          ),
          alt: [
            {
              title: 'The code was in the link',
              render: missing(
                'Arrived on a partner link',
                'Not drawn. The code can be in the link rather than typed. It is checked before anything is on screen, so there is nothing to fill in and the only thing this step can do is fail.',
              ),
            },
          ],
        },
        {
          id: 'partner-org',
          title: 'Create your organisation',
          tap: 'The code is accepted',
          render: missing(
            'Create your organisation',
            'Not drawn. Name, phone, and whichever codes the product asks for. This is the step where an adviser stops being a member and becomes an organisation owner.',
          ),
        },
        {
          id: 'partner-portal',
          title: 'The portal, not the app',
          tap: 'Organisation created',
          render: missing(
            'The portal',
            'Not drawn here, and not drawn anywhere: the partner portal has an overview page and no prototype. An adviser refers clients from it.',
          ),
        },
      ],
    },
    {
      journey: 'Family',
      title: 'Invited into someone else’s circle',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              A member invites a sibling. They join a circle that exists rather
              than starting one, and because they are not the primary caregiver
              the questions are skipped: there is nothing to ask that the person
              who set the circle up has not answered.
            </span>
            <span>
              So this is the only route that goes from an email to the app
              without a single question in between, and the app they land in was
              set up around somebody else’s answers.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'circle-email',
          title: 'The care circle email',
          tap: 'A member invites them',
          render: missing(
            'The care circle invite',
            '"[Name] has invited you to join their care circle." Not drawn. The only email where the sender is another member rather than us or an organisation.',
          ),
        },
        {
          id: 'circle-kinde',
          title: 'Create account (Kinde)',
          tap: 'Tap the link',
          render: () => kindeRegister,
        },
        {
          id: 'circle-link',
          title: 'Coming back on the link',
          tap: 'Set a password',
          render: arrivingOnALink,
        },
        {
          id: 'circle-home',
          title: 'Straight into the app',
          tap: 'The token is accepted',
          render: missing(
            'Somebody else’s circle',
            'Not drawn as a variant. They land on the app the master prototype walks, with no questions asked and a care record they did not fill in. What a joining member sees first is an open question.',
          ),
        },
      ],
    },
    {
      journey: 'Self-serve',
      title: 'Typeform first, account second',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The only route where somebody arrives with no invite and no
              employer. It works by taking the answers before the account
              exists: a Typeform on the site or under an ad, then registration,
              then nothing to ask.
            </span>
            <span>
              The link back carries a marker saying the questions are done, so
              onboarding has no steps and they are marked complete on arrival.
              Every screen we design for the questions is skipped on this route.
            </span>
          </Stack>
        </Note>
      ),
      noteAfter: (
        <Note title='Bears on Tell Us Once' colour='sunflower' variant='body2'>
          Not a fault in getting in, but worth knowing while intake is still
          moving: this route and the one below it never see our questions.
          Whatever Tell Us Once becomes, it will not reach self-serve leads or
          the members of an organisation running its own form.
        </Note>
      ),
      steps: [
        {
          id: 'typeform',
          title: 'The Typeform',
          tap: 'From the site or an ad',
          render: missing(
            'A Typeform we do not own',
            'Not ours to draw. It sits outside the app and asks the questions the app would have asked, in Typeform’s patterns rather than ours.',
          ),
        },
        {
          id: 'typeform-kinde',
          title: 'Create account (Kinde)',
          tap: 'Submit the form',
          render: () => kindeRegister,
        },
        {
          id: 'typeform-skip',
          title: 'Nothing to ask',
          tap: 'Set a password',
          render: missing(
            'Questions skipped',
            'Not drawn. Onboarding has no steps on this route, so whatever the questions are for is carried entirely by the Typeform. There is no screen between the account and the app.',
          ),
        },
      ],
    },
    {
      journey: 'Self-serve',
      title: 'The organisation’s own questions',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Not a way in. A way through the questions, for the clients who
              wanted their own.
            </span>
            <span>
              Where an organisation has a Typeform of its own, the whole
              question flow is replaced by it embedded in the screen. It is
              behind a flag and only for the primary caregiver. A question we
              add to our flow does not reach these members.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'org-typeform',
          title: 'A Typeform in place of ours',
          tap: 'Access verified',
          render: missing(
            'The organisation’s Typeform',
            'Not drawn. Our questions are not shown at all: the screen holds their form instead, so this route sees none of the intake design.',
          ),
        },
      ],
    },
    {
      journey: 'KareHero admin',
      title: 'We make the account',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The route that exists because the others fail. An admin creates
              the account, or verifies one by hand when a check has said no and
              the person should be in anyway.
            </span>
            <span>
              Worth knowing it is there before designing an error state with no
              way out of it.
            </span>
          </Stack>
        </Note>
      ),
      noteAfter: (
        <Note title='Problem' colour='cherry' variant='body2'>
          Every access failure we have drawn ends in a message under the field
          and a suggestion to try again. For somebody the checks will keep
          rejecting, this is the only way through, and nothing on any screen
          points at it.
        </Note>
      ),
      steps: [
        {
          id: 'admin-create',
          title: 'New account',
          tap: 'A member cannot get in',
          render: missing(
            'New account (admin)',
            'Not drawn. Email, role, organisation. These screens are on main, nobody is redesigning them, and they are desktop.',
          ),
        },
        {
          id: 'admin-verify',
          title: 'Verify by hand',
          tap: 'The account exists but is unverified',
          render: missing(
            'Verify the account',
            'Not drawn. Sets the organisation and role on somebody the automatic checks would not let through.',
          ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
